import React from 'react';
import { Button, Card, Pagination, Table, TableColumn, PermissionGate } from '@/components/common';
import { GroupDTO, InstitutionDTO, PaginationMetadata } from '@/models';
import { usePermissions } from '@/hooks';

interface GroupTableProps {
  groups: GroupDTO[];
  institutions: InstitutionDTO[];
  pagination: PaginationMetadata | null;
  currentPage: number;
  pageSize: number;
  pageSizeOptions: readonly number[];
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onEdit: (group: GroupDTO) => void;
  onDelete: (group: GroupDTO) => void;
}

export const GroupTable: React.FC<GroupTableProps> = ({
  groups,
  institutions,
  pagination,
  currentPage,
  pageSize,
  pageSizeOptions,
  isLoading,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
}) => {
  const { canPerform } = usePermissions();

  const getInstitutionName = (institutionId: string) => {
    const inst = institutions.find((i) => i.id === institutionId);
    return inst?.name || '-';
  };

  const columns: TableColumn<GroupDTO>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (group) => group.name || '-',
    },
    {
      key: 'institution',
      header: 'Institution',
      render: (group) => getInstitutionName(group.institutionId),
    },
    {
      key: 'city',
      header: 'City',
      render: (group) => group.city || '-',
    },
    {
      key: 'province',
      header: 'Province',
      render: (group) => group.province || '-',
    },
    {
      key: 'phoneNumber',
      header: 'Phone',
      render: (group) => group.phoneNumber || '-',
    },
    ...(canPerform('group:edit') || canPerform('group:delete')
      ? [
          {
            key: 'actions' as const,
            header: 'Actions',
            width: '150px',
            render: (group: GroupDTO) => (
              <div className="d-flex gap-2">
                <PermissionGate action="group:edit">
                  <Button
                    variant="primary"
                    size="sm"
                    icon="fas fa-edit"
                    onClick={() => onEdit(group)}
                  >
                    Edit
                  </Button>
                </PermissionGate>
                <PermissionGate action="group:delete">
                  <Button
                    variant="danger"
                    size="sm"
                    icon="fas fa-trash"
                    onClick={() => onDelete(group)}
                  />
                </PermissionGate>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <Card className="border-0 shadow-sm">
      <Table
        columns={columns}
        data={groups}
        keyExtractor={(group) => group.id}
        isLoading={isLoading}
        emptyMessage="No groups found. Click 'Add Group' to create one."
      />
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="d-flex align-items-center gap-2">
          <small className="text-muted">Rows per page:</small>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          {pagination && (
            <small className="text-muted">
              Showing {(currentPage - 1) * pageSize + 1}–
              {Math.min(currentPage * pageSize, pagination.totalCount)} of {pagination.totalCount}{' '}
              groups
            </small>
          )}
        </div>
        <Pagination pagination={pagination} onPageChange={onPageChange} />
      </div>
    </Card>
  );
};
